import React, { useMemo } from 'react';
import { CardTitle, CardSubhead } from '@spark/ui-components/components/Card';
import { ButtonWithLoading } from '@spark/ui-components/components/Button';
import BottomStatusFilter from '@spark/ui-components/components/BottomStatusFilter';
import Icon from '@spark/ui-components/components/Icon';
import Tooltip from '@spark/ui-components/components/Tooltip';
import styled from 'styled-components';

const formatCurrency = (value) => {
  const numericValue = Number(value ?? 0);
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(Number.isFinite(numericValue) ? numericValue : 0);
};

const DepositoNotasHeader = ({
  totalRegistros,
  onAdicionar,
  onExportarPendencias,
  exportarPendenciasLoading,
  onMostrarDocumentacao,
  documentacaoLoading,
  onMostrarAuditoriaGeral,
  auditoriaGeralLoading,
  onMostrarAuditoriaTabelaPreco,
  auditoriaTabelaPrecoLoading,
  onClientes,
  clientesLoading,
  onGerarCobrancaPaletizacao,
  onGerarCobrancaEtiquetagem,
  onGerarCobrancaArmazenagem,
  onGerarCobrancaAvulsa,
  bulkCobrancaLoading,
  onArmazenamentoLote,
  armazenamentoLoteLoading,
  onAlterarLote,
  alteracaoLoteLoading,
  onPendenciaChange,
  pendenciaValue,
  pendenciaCounts,
  selectedCount = 0
}) => {
  const pendenciaOptions = useMemo(() => ([
    {
      value: 'cobranca_paletizacao',
      label: 'Cob. Paletização',
      icon: 'currency-usd',
      count: pendenciaCounts?.cobPaletizacao ?? 0,
      amount: formatCurrency(pendenciaCounts?.valorCobPaletizacao),
      obs: 'Setor Responsável: Emissão'
    },
    {
      value: 'cobranca_etiquetagem',
      label: 'Cob. Etiquetagem',
      icon: 'clipboard-text',
      count: pendenciaCounts?.cobEtiquetagem ?? 0,
      amount: formatCurrency(pendenciaCounts?.valorCobEtiquetagem),
      obs: 'Setor Responsável: Emissão'
    },
    {
      value: 'cobranca_armazenagem',
      label: 'Cob. Armazenamento',
      icon: 'warehouse',
      count: pendenciaCounts?.cobArmazenamento ?? 0,
      amount: formatCurrency(pendenciaCounts?.valorCobArmazenamento),
      obs: 'Setor Responsável: Emissão'
    },
    {
      value: 'sem_tabela_preco',
      label: 'Sem Tabela Preço',
      icon: 'database-off',
      count: pendenciaCounts?.semTabelaPreco ?? 0,
      obs: 'Setor Responsável: Comercial'
    },
    {
      value: 'agendamento',
      label: 'Agendamento',
      icon: 'calendar-check',
      count: pendenciaCounts?.agendamento ?? 0,
      obs: 'Setor Responsável: Atendimento'
    },
    {
      value: 'saida',
      label: 'Saída/Entrega',
      icon: 'truck-delivery',
      count: pendenciaCounts?.saida ?? 0,
      obs: 'Setor Responsável: Depósito'
    },
    {
      value: 'finalizacao',
      label: 'Finalização',
      icon: 'check',
      count: pendenciaCounts?.finalizacao ?? 0,
      obs: 'Setor Responsável: Emissão'
    },
    {
      value: 'divergencia',
      label: 'Divergência',
      icon: 'alert-circle',
      count: pendenciaCounts?.divergencia ?? 0,
      obs: 'Setor Responsável: Depósito'
    },
    {
      value: 'qtd_etiqueta',
      label: 'Qtd. Etiqueta',
      icon: 'label',
      count: pendenciaCounts?.qtdEtiqueta ?? 0,
      obs: 'Setor Responsável: Depósito'
    },
    {
      value: 'qtd_palete',
      label: 'Qtd. Palete',
      icon: 'package-variant',
      count: pendenciaCounts?.qtdPalete ?? 0,
      obs: 'Setor Responsável: Depósito'
    }
  ]), [pendenciaCounts]);

  return (
    <HeaderWrap>
      <HeaderContent>
        <div>
          <TitleRow>
            <CardTitle className="mb-0">Deposito</CardTitle>
            {onMostrarDocumentacao && (
              <Tooltip dir="top" text="Ajuda do modulo">
                <span className="d-inline-flex align-items-center">
                  <HelpButton
                    type="button"
                    onClick={onMostrarDocumentacao}
                    disabled={documentacaoLoading}
                    aria-label="Ajuda do modulo deposito"
                  >
                    <Icon name={documentacaoLoading ? 'loading' : 'information-outline'} size={16} />
                  </HelpButton>
                </span>
              </Tooltip>
            )}
            {onMostrarAuditoriaGeral && (
              <Tooltip dir="top" text="Auditoria geral">
                <span className="d-inline-flex align-items-center">
                  <HelpButton
                    type="button"
                    onClick={onMostrarAuditoriaGeral}
                    disabled={auditoriaGeralLoading}
                    aria-label="Auditoria geral do modulo deposito"
                  >
                    <Icon name={auditoriaGeralLoading ? 'loading' : 'eye'} size={16} />
                  </HelpButton>
                </span>
              </Tooltip>
            )}
            {onMostrarAuditoriaTabelaPreco && (
              <Tooltip dir="top" text="Auditoria tabela de preco">
                <span className="d-inline-flex align-items-center">
                  <HelpButton
                    type="button"
                    onClick={onMostrarAuditoriaTabelaPreco}
                    disabled={auditoriaTabelaPrecoLoading}
                    aria-label="Auditoria geral de tabela de preco do modulo deposito"
                  >
                    <Icon name={auditoriaTabelaPrecoLoading ? 'loading' : 'eye'} size={16} />
                  </HelpButton>
                </span>
              </Tooltip>
            )}
          </TitleRow>
          <CardSubhead>Total de registros: {totalRegistros}</CardSubhead>
        </div>

        {onPendenciaChange && (
          <PendenciasWrap>
            <PendenciasTitle>Pendências:</PendenciasTitle>
            <BottomStatusFilter
              options={pendenciaOptions}
              value={pendenciaValue}
              onChange={onPendenciaChange}
            />
          </PendenciasWrap>
        )}
      </HeaderContent>

      <ButtonContainer>
        {onGerarCobrancaPaletizacao && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onGerarCobrancaPaletizacao}
            loading={bulkCobrancaLoading === 'paletizacao'}
            disabled={selectedCount === 0}
          >
            Gerar Cobranca Paletizacao
          </ButtonWithLoading>
        )}
        {onGerarCobrancaEtiquetagem && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onGerarCobrancaEtiquetagem}
            loading={bulkCobrancaLoading === 'etiquetagem'}
            disabled={selectedCount === 0}
          >
            Gerar Cobranca Etiquetagem
          </ButtonWithLoading>
        )}
        {onGerarCobrancaArmazenagem && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onGerarCobrancaArmazenagem}
            loading={bulkCobrancaLoading === 'armazenagem'}
            disabled={selectedCount === 0}
          >
            Gerar Cobranca Armazenagem
          </ButtonWithLoading>
        )}
        {onGerarCobrancaAvulsa && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onGerarCobrancaAvulsa}
            loading={bulkCobrancaLoading === 'avulsa'}
            disabled={selectedCount === 0}
          >
            Gerar Cobranca Avulsa
          </ButtonWithLoading>
        )}
        {onArmazenamentoLote && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onArmazenamentoLote}
            loading={armazenamentoLoteLoading}
            disabled={selectedCount === 0}
          >
            Armazenamento
          </ButtonWithLoading>
        )}
        {onAlterarLote && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onAlterarLote}
            loading={alteracaoLoteLoading}
            disabled={selectedCount === 0}
          >
            Alterar em Lote
          </ButtonWithLoading>
        )}
        {onExportarPendencias && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onExportarPendencias}
            loading={exportarPendenciasLoading}
            disabled={totalRegistros === 0}
          >
            Exportar Pendencias
          </ButtonWithLoading>
        )}
        {onClientes && (
          <ButtonWithLoading
            variant="outline-primary"
            onClick={onClientes}
            loading={clientesLoading}
          >
            Clientes
          </ButtonWithLoading>
        )}
        {onAdicionar && (
          <ButtonWithLoading variant="primary" onClick={onAdicionar}>
            Adicionar
          </ButtonWithLoading>
        )}
      </ButtonContainer>
    </HeaderWrap>
  );
};

export default DepositoNotasHeader;

const HeaderWrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 992px) {
    flex-direction: column;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

const TitleRow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const PendenciasWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PendenciasTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: #495057;
`;

const HelpButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  color: inherit;
  opacity: ${(props) => (props.disabled ? 0.6 : 1)};

  &:hover {
    opacity: 0.8;
  }
`;
